/**
 * permissionRequest.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";

import MuiAlert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
//import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import SnackbarContent from "@mui/material/SnackbarContent";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";

interface Props {
  onDoEnableNotifications(): Promise<boolean>
}

export function needNotificationRequest(): boolean {
  return "PushManager" in window && Notification.permission === "default"
    || "safari" in window && "pushNotification" in (window as any).safari &&
      (window as any).safari.pushNotification.permission(secrets.SAFARI_PUSH_ID).permission === "default"
  ;
}

export function NotificationPermissionRequestComponent(props: Props): React.ReactElement<any, any> {
  const [open, setOpen] = React.useState<boolean>(needNotificationRequest());
  const [finishedEnabling, setFinished] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");

  async function doEnableNotifications() {
    try {
      await props.onDoEnableNotifications();
      setFinished(true);
    } catch (err) {
      console.error("Couldn't enable notifications:", err);
      setErrMsg("An Error occurred registering for notifications");
    }
  }

  const content = finishedEnabling ? <>
    <Typography variant="h4">Finished</Typography>
    <DialogContent>
      <p>
        All set. You may still need to enable notifications
        in your system.
      </p>
    </DialogContent>
    <DialogActions>
      <Button color="primary" disableRipple disableTouchRipple
          onClick={() => setOpen(false)}>Close</Button>
    </DialogActions>
  </> : <>
    <Typography variant="h4">Enable Push Notifications</Typography>
    <DialogContent>
      {errMsg && <MuiAlert variant="filled" severity="error">{errMsg}</MuiAlert>}
      <p>We use push notifications
        to update you when new messages arrive.</p>
      <p>Can we enable notifications?</p>
    </DialogContent>
    <DialogActions>
      <Button color="primary" disableRipple disableTouchRipple
          onClick={() => setOpen(false)}>Close</Button>
      <Button color="primary" variant="contained"
          disableRipple disableTouchRipple
          onClick={doEnableNotifications}>Enable</Button>
    </DialogActions>
  </>;

  return open ? <Snackbar open={open}
        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
        onClose={() => { setOpen(false);}}
        TransitionComponent={props => <Slide {...props} direction="right" />}>
    <SnackbarContent message={content} />
  </Snackbar> : <></>;
}

interface StoreProps {
  doEnablePersistentStorage(): Promise<void>
}

export function PersistentStorePermissionRequestComponent(props: StoreProps): React.ReactElement<any, any> {
  const [open, setOpen] = React.useState<boolean>(false);

  React.useEffect(function () {
    if (navigator.storage && navigator.storage.persist as any) {
      navigator.storage.persisted().then(isPersisted => {
        setOpen(!isPersisted);
      }, err => {
        console.error("Error determining if persisted:", err);
      })
    }
  }, []);

  async function doEnablePersistent () {
    await props.doEnablePersistentStorage();
    setOpen(false);
  }

  const content = <>
    <Typography variant="h4">Enable Persistent Browser Storage</Typography>
    <DialogContent>
      <p>We store cryptographic secrets and message histories locally in your
        browser's storage. If not persistent, your browser may decide to delete
        this storage at will.
      </p>
      <p>Can we enable persistent storage?</p>
    </DialogContent>
    <DialogActions>
      <Button color="primary" disableRipple disableTouchRipple
          onClick={() => setOpen(false)}>Close</Button>
      <Button color="primary"
          disableRipple disableTouchRipple
          onClick={doEnablePersistent}>Enable</Button>
    </DialogActions>
  </>;

  return open ? <Snackbar open={open}
      className="persistent-store-request-frame"
      anchorOrigin={{vertical: "bottom", horizontal: "left"}}
      onClose={() => { setOpen(false);}}
      TransitionComponent={props => <Slide {...props} direction="right" />}>
    <SnackbarContent message={content} />
  </Snackbar> : <></>;
}
